import { useEffect, useState } from "react";
import axiosInstance from "../utils/axiosInstance";
import { countryOptions } from "../utils/Countries";
import {
  Alert,
  Button,
  Field,
  Input,
  InfoTile,
  Page,
  PageHeader,
  PageLoader,
  Panel,
  SectionHeading,
  Select,
  Tabs,
} from "../components/ui";

const AccountSettings = () => {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState("");

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [passwordStrength, setPasswordStrength] = useState("");

  const [loading, setLoading] = useState(true);
  const [changingPassword, setChangingPassword] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState("personal");

  const handleCountryChange = (e) => {
    const selectedCountry = e.target.value;
    const countryData = countryOptions.find(
      (option) => option.name === selectedCountry
    );
    setCountry(selectedCountry);
    setPhone(countryData ? countryData.code : "");
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axiosInstance.get("/users/my");
        setUsername(response.data.username);
        setEmail(response.data.email);
        setPhone(response.data.phone);
        setCountry(response.data.country);
        setLoading(false);
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, []);

  const handlePersonalInfoSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await axiosInstance.put("/users/my", {
        username: username,
        phone: `${phone}`,
        country: country,
      });

      if (response.status === 200) {
        setSuccess("Details updated successfully!");
      } else {
        setError("Failed to update details.");
      }
    } catch (error) {
      if (
        error.response &&
        error.response.status &&
        error.response.status === 400
      ) {
        setError(error.response.data.message);
        return;
      }
      setError("An error occurred while updating.");
    } finally {
      setLoading(false);
    }
  };

  const evaluatePasswordStrength = (password) => {
    let strength = "weak";
    if (password.length >= 8) {
      const hasUpperCase = /[A-Z]/.test(password);
      const hasLowerCase = /[a-z]/.test(password);
      const hasNumber = /\d/.test(password);
      const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

      if (hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar) {
        strength = "strong";
      } else if (hasUpperCase || hasNumber || hasSpecialChar) {
        strength = "normal";
      }
    }
    return strength;
  };

  const handleNewPasswordChange = (e) => {
    const pwd = e.target.value;
    setNewPassword(pwd);
    const strength = evaluatePasswordStrength(pwd);
    setPasswordStrength(strength);
  };

  const handlePasswordChangeSubmit = async (e) => {
    e.preventDefault();
    setChangingPassword(true);
    setError(null);
    setSuccess(null);

    if (newPassword !== confirmNewPassword) {
      setError("New passwords do not match.");
      setChangingPassword(false);
      return;
    }

    try {
      const response = await axiosInstance.put("/users/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });

      if (response.status === 200) {
        setSuccess("Password changed successfully!");
      } else {
        setError("Failed to change password.");
      }
    } catch (error) {
      if (
        error.response &&
        error.response.status &&
        error.response.status === 400
      ) {
        setError(error.response.data.message);
        return;
      }
      setError("An error occurred while changing password.");
    } finally {
      setChangingPassword(false);
    }
  };

  // The meter reads left to right: a weak password fills a third in rose, a
  // strong one fills the bar in emerald.
  const strengthMeter = {
    strong: { width: "w-full", bar: "bg-emerald-500", text: "text-emerald-600 dark:text-emerald-400" },
    normal: { width: "w-2/3", bar: "bg-amber-500", text: "text-amber-600 dark:text-amber-400" },
    weak: { width: "w-1/3", bar: "bg-rose-500", text: "text-rose-600 dark:text-rose-400" },
  }[passwordStrength || "weak"];

  if (loading) {
    return (
      <Page width="max-w-3xl">
        <PageHeader title="Account Settings" />
        <PageLoader />
      </Page>
    );
  }

  return (
    <Page width="max-w-3xl">
      <PageHeader title="Account Settings" subtitle={email} />

      <Tabs
        value={activeTab}
        onChange={(tab) => {
          setActiveTab(tab);
          setError(null);
          setSuccess(null);
        }}
        items={[
          { id: "personal", label: "Personal Information" },
          { id: "password", label: "Change Password" },
        ]}
      />

      {error ? <Alert tone="error">{error}</Alert> : null}
      {success ? <Alert tone="success">{success}</Alert> : null}

      {activeTab === "personal" && (
        <Panel>
          <SectionHeading title="Edit Personal Information" />
          <form onSubmit={handlePersonalInfoSubmit} className="mt-5 space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <InfoTile label="Username (not changeable)" value={username} />
              <InfoTile label="Email (not changeable)" value={email} />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Country" required>
                <Select value={country} onChange={handleCountryChange} required>
                  <option value="" disabled>
                    Select Country
                  </option>
                  {countryOptions.map((option) => (
                    <option key={option.name} value={option.name}>
                      {option.name}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field label="Phone Number" required>
                <Input
                  type="tel"
                  placeholder="Phone Number"
                  value={phone}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^[0-9+\- ]*$/.test(value)) {
                      setPhone(value);
                    }
                  }}
                  required
                />
              </Field>
            </div>

            <div className="flex justify-end">
              <Button type="submit" disabled={loading}>
                {loading ? "Saving…" : "Save Changes"}
              </Button>
            </div>
          </form>
        </Panel>
      )}

      {activeTab === "password" && (
        <Panel>
          <SectionHeading title="Change Password" />
          <form onSubmit={handlePasswordChangeSubmit} className="mt-5 space-y-4">
            <Field label="Current Password" required>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                required
              />
            </Field>

            <Field label="New Password" required>
              <Input
                type="password"
                value={newPassword}
                onChange={handleNewPasswordChange}
                placeholder="Enter new password"
                required
              />
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-[#2a2a2c]">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${strengthMeter.width} ${
                    newPassword ? strengthMeter.bar : "w-0"
                  }`}
                />
              </div>
              {passwordStrength && newPassword ? (
                <p className={`mt-1.5 text-xs font-bold uppercase tracking-[0.08em] ${strengthMeter.text}`}>
                  Password strength: {passwordStrength}
                </p>
              ) : (
                <p className="mt-1.5 text-xs text-gray-400 dark:text-slate-500">
                  At least 8 characters, mixing case, a number and a symbol.
                </p>
              )}
            </Field>

            <Field label="Confirm New Password" required>
              <Input
                type="password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                placeholder="Confirm new password"
                required
              />
            </Field>

            <div className="flex justify-end">
              <Button type="submit" disabled={changingPassword}>
                {changingPassword ? "Saving…" : "Change Password"}
              </Button>
            </div>
          </form>
        </Panel>
      )}
    </Page>
  );
};

export default AccountSettings;
